// Builder puro — Libro IVA Digital COMPRAS (subdiario compras, RG 3685).
// Genera los 2 TXT de ancho fijo oficiales desde los comprobantes fiscales
// recibidos persistidos en pos.expenses (bloque invoice_*).
// Layout oficial ("Diseño de registros" del portal Libro IVA Digital):
//   COMPRAS_CBTE      → 325 posiciones por comprobante recibido
//   COMPRAS_ALICUOTAS → 84 posiciones por alícuota discriminada
//
// Diferencias vs VENTAS (libroIvaTxt.ts):
//  - Incluye identificación del VENDEDOR (código doc + CUIT + denominación) —
//    el libro compras registra quién te facturó.
//  - Registra el crédito fiscal computable (campo 21) en vez de la liquidación.
//  - Cantidad de alícuotas (campo 19): comprobantes B/C NO discriminan IVA →
//    se informa '0' y el comprobante no genera filas en COMPRAS_ALICUOTAS.
//  - Código de operación (campo 20): en compras el valor "no corresponde" es
//    un espacio en blanco; 'E' = exentas, 'N' = no gravadas.
//  - NC/ND de proveedor se registran con su tipo (3/8/13/53 y 2/7/12/52) y
//    montos positivos — el tipo ya indica la naturaleza.
// Golden tests → libroIvaComprasTxt.test.ts

import { ALICUOTA_CODE, type LibroIvaBreakdown } from './libroIvaTxt'

export type LibroIvaCompraAlicuota = { id: number; baseImp: number; importe: number }

// Percepciones/retenciones sufridas por slot del diseño oficial (campos 12-15).
export type LibroIvaCompraPercepciones = {
  iva: number         // campo 12: percepciones/pagos a cuenta de IVA
  nacionales: number  // campo 13: otros impuestos nacionales
  iibb: number        // campo 14: ingresos brutos provinciales
  municipales: number // campo 15: impuestos municipales
}

export type LibroIvaCompraRow = {
  expenseDate: string // 'YYYY-MM-DD' — fecha de emisión del comprobante (campo 1)
  invoiceDocType: number // tipo comprobante compras: 1=A 6=B 11=C 51=M, NC 3/8/13/53, ND 2/7/12/52
  invoicePv: number
  invoiceNumber: number
  supplierTaxId: string | null // CUIT vendedor (snapshot del comprobante)
  supplierName: string | null  // denominación vendedor (snapshot)
  amount: number // total registrado; se usa como fallback cuando no hay desglose
  breakdown: LibroIvaBreakdown | null // desglose discriminado (A/M); null en B/C
  perceptions: LibroIvaCompraPercepciones | null
}

export const LIBRO_IVA_COMPRAS_CBTE_ROW_LENGTH = 325
export const LIBRO_IVA_COMPRAS_ALICUOTAS_ROW_LENGTH = 84

const digitsOnly = (s: string) => s.replace(/\D/g, '')
const padLeftZeros = (s: string, len: number) => digitsOnly(s).padStart(len, '0').slice(-len)

// Importe N(15): 13 enteros + 2 decimales, sin separadores, pad-left ceros.
const money = (n: number) => padLeftZeros((Math.round(Math.abs(n) * 100)).toString(), 15)
// Alfa: strip diacríticos + uppercase + pad-right espacios (misma normalización que ventas).
const alfa = (s: string, len: number) =>
  (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Z0-9 .,'&()-]/gi, '')
    .toUpperCase()
    .padEnd(len, ' ')
    .slice(0, len)
const fmtDate = (yyyyMmDd: string) => yyyyMmDd.replace(/-/g, '').slice(0, 8)

// Orden determinístico: fecha, tipo, PV, número (mismo criterio que ventas).
const sortRows = (rows: LibroIvaCompraRow[]) =>
  [...rows].sort(
    (a, b) =>
      a.expenseDate.localeCompare(b.expenseDate) ||
      a.invoiceDocType - b.invoiceDocType ||
      a.invoicePv - b.invoicePv ||
      a.invoiceNumber - b.invoiceNumber
  )

// Código documento vendedor (tabla Documentos): 80 CUIT, 96 DNI, 99 sin identificar.
export const vendorDocCode = (taxId: string | null): string => {
  const d = digitsOnly(taxId ?? '')
  if (d.length === 11) return '80'
  if (d.length >= 7 && d.length <= 8) return '96'
  return '99'
}

const alicuotasOf = (row: LibroIvaCompraRow): LibroIvaCompraAlicuota[] =>
  (row.breakdown?.alicuotas ?? []).filter((a) => a.id in ALICUOTA_CODE)

const percep = (row: LibroIvaCompraRow): LibroIvaCompraPercepciones => ({
  iva: row.perceptions?.iva ?? 0,
  nacionales: row.perceptions?.nacionales ?? 0,
  iibb: row.perceptions?.iibb ?? 0,
  municipales: row.perceptions?.municipales ?? 0,
})

// Campo 9 — total operación: suma de componentes fiscales cuando hay desglose
// (neto + IVA + exento + no gravado + percepciones); si no, el monto registrado.
const totalOperacion = (row: LibroIvaCompraRow): number => {
  const b = row.breakdown
  const p = percep(row)
  if (!b) return row.amount + p.iva + p.nacionales + p.iibb + p.municipales
  return (b.impNeto ?? 0) + (b.impIva ?? 0) + (b.impOpEx ?? 0) + (b.impTotConc ?? 0)
    + p.iva + p.nacionales + p.iibb + p.municipales
}

// Campo 20 — código operación: ' ' normal; 'E' si toda la operación es exenta;
// 'N' si es toda no gravada. (Tabla Código de operación — compras.)
const operationCode = (row: LibroIvaCompraRow): string => {
  const b = row.breakdown
  if (!b) return ' '
  const neto = b.impNeto ?? 0
  const iva = b.impIva ?? 0
  const exento = b.impOpEx ?? 0
  const noGravado = b.impTotConc ?? 0
  if (neto === 0 && iva === 0 && exento > 0 && noGravado === 0) return 'E'
  if (neto === 0 && iva === 0 && exento === 0 && noGravado > 0) return 'N'
  return ' '
}

// ── COMPRAS_CBTE (325 posiciones) ────────────────────────────────────────────
export const buildComprasCbteTxt = (rows: LibroIvaCompraRow[]): string =>
  sortRows(rows).map((row) => {
    const b = row.breakdown
    const p = percep(row)
    const nAlicuotas = alicuotasOf(row).length
    const doc = vendorDocCode(row.supplierTaxId)
    const creditoFiscal = nAlicuotas > 0 ? (b?.impIva ?? 0) : 0
    return [
      fmtDate(row.expenseDate),                                    //  1-  8 (8)  fecha emisión
      padLeftZeros(String(row.invoiceDocType), 3),                 //  9- 11 (3)  tipo comprobante
      padLeftZeros(String(row.invoicePv), 5),                      // 12- 16 (5)  punto de venta
      padLeftZeros(String(row.invoiceNumber), 20),                 // 17- 36 (20) número comprobante
      ' '.repeat(16),                                              // 37- 52 (16) despacho importación
      doc,                                                         // 53- 54 (2)  código doc vendedor
      doc === '99' ? '0'.repeat(20) : padLeftZeros(row.supplierTaxId ?? '', 20), // 55-74 (20) id vendedor
      alfa(row.supplierName ?? '', 30),                            // 75-104 (30) denominación vendedor
      money(totalOperacion(row)),                                  // 105-119 (15) total operación
      money(b?.impTotConc ?? 0),                                   // 120-134 (15) no gravado
      money(b?.impOpEx ?? 0),                                      // 135-149 (15) exento
      money(p.iva),                                                // 150-164 (15) percep. IVA
      money(p.nacionales),                                         // 165-179 (15) percep. nacionales
      money(p.iibb),                                               // 180-194 (15) percep. IIBB
      money(p.municipales),                                        // 195-209 (15) percep. municipales
      money(0),                                                    // 210-224 (15) impuestos internos
      'PES',                                                       // 225-227 (3)  moneda
      '0001000000',                                                // 228-237 (10) tipo cambio
      String(nAlicuotas),                                          // 238     (1)  cantidad alícuotas
      operationCode(row),                                          // 239     (1)  código operación
      money(creditoFiscal),                                        // 240-254 (15) crédito fiscal computable
      money(0),                                                    // 255-269 (15) otros tributos
      '0'.repeat(11),                                              // 270-280 (11) CUIT emisor/corredor
      ' '.repeat(30),                                              // 281-310 (30) denominación corredor
      money(0),                                                    // 311-325 (15) IVA comisión
    ].join('')
  }).join('\r\n')

// ── COMPRAS_ALICUOTAS (84 posiciones) ────────────────────────────────────────
// Solo comprobantes que discriminan IVA (A/M, NC/ND A/M). B/C no entran.
export const buildComprasAlicuotasTxt = (rows: LibroIvaCompraRow[]): string => {
  const lines: string[] = []
  for (const row of sortRows(rows)) {
    const doc = vendorDocCode(row.supplierTaxId)
    for (const a of alicuotasOf(row)) {
      lines.push([
        padLeftZeros(String(row.invoiceDocType), 3),               //  1-  3 (3)  tipo comprobante
        padLeftZeros(String(row.invoicePv), 5),                    //  4-  8 (5)  punto de venta
        padLeftZeros(String(row.invoiceNumber), 20),               //  9- 28 (20) número comprobante
        doc,                                                       // 29- 30 (2)  código doc vendedor
        doc === '99' ? '0'.repeat(20) : padLeftZeros(row.supplierTaxId ?? '', 20), // 31-50 (20) id vendedor
        money(a.baseImp),                                          // 51-65 (15) neto gravado
        ALICUOTA_CODE[a.id],                                       // 66-69 (4)  alícuota
        money(a.importe),                                          // 70-84 (15) impuesto liquidado
      ].join(''))
    }
  }
  return lines.join('\r\n')
}
